// footer-accordion.js
/**
 * Footer Accordion Component
 * Manages smooth expand/collapse animations for footer accordion panels
 * Inspired by expandable-custom.js and gpt-product-selection-modal.js
 */
class FooterAccordion {
  constructor(container) {
    this.container = container;
    this.panels = Array.from(container.querySelectorAll('.footer-accordion__panel'));
    this.singleOpen = container.dataset.singleOpen === 'true';
    this.animationDuration = 300;
    this.animationEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    
    this.init();
  }

  init() {
    // Initialize each panel
    this.panels.forEach((panel) => {
      const button = panel.querySelector('.footer-accordion__button');
      const content = panel.querySelector('.footer-accordion__content');
      const contentInner = content?.querySelector('.footer-accordion__content-inner');
      
      if (!button || !content || !contentInner) return;

      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      
      // Set initial state
      if (isExpanded) {
        this.setExpanded(panel, true, false); // false = no animation on init
      } else {
        this.setExpanded(panel, false, false);
      }

      // Add click handler
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.togglePanel(panel);
      });

      // Add keyboard handler
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.togglePanel(panel);
        }
      });
    });
  }

  togglePanel(panel) {
    const button = panel.querySelector('.footer-accordion__button');
    if (!button) return;

    const isCurrentlyExpanded = button.getAttribute('aria-expanded') === 'true';
    const willBeExpanded = !isCurrentlyExpanded;

    // If single open mode and opening a panel, close others first
    if (this.singleOpen && willBeExpanded) {
      this.panels.forEach((p) => {
        if (p !== panel) {
          const otherButton = p.querySelector('.footer-accordion__button');
          if (otherButton?.getAttribute('aria-expanded') === 'true') {
            this.setExpanded(p, false, true);
          }
        }
      });
    }

    // Toggle the clicked panel
    this.setExpanded(panel, willBeExpanded, true);
  }

  setExpanded(panel, expanded, animate = true) {
    const button = panel.querySelector('.footer-accordion__button');
    const content = panel.querySelector('.footer-accordion__content');
    const contentInner = content?.querySelector('.footer-accordion__content-inner');
    
    if (!button || !content || !contentInner) return;

    // Update ARIA attributes
    button.setAttribute('aria-expanded', expanded.toString());
    content.setAttribute('aria-hidden', (!expanded).toString());

    if (expanded) {
      // Opening
      this.animateOpen(content, contentInner, animate);
    } else {
      // Closing
      this.animateClose(content, contentInner, animate);
    }
  }

  animateOpen(content, contentInner, animate) {
    if (!animate) {
      // No animation - set directly
      const height = contentInner.scrollHeight;
      content.style.height = `${height}px`;
      content.style.visibility = 'visible';
      content.style.overflow = 'visible';
      return;
    }

    // Get target height
    const targetHeight = contentInner.scrollHeight;
    
    // Set initial state
    content.style.height = '0px';
    content.style.overflow = 'hidden';
    content.style.visibility = 'visible';
    
    // Force reflow
    void content.offsetHeight;
    
    // Animate to full height
    requestAnimationFrame(() => {
      content.style.transition = `height ${this.animationDuration}ms ${this.animationEasing}`;
      content.style.height = `${targetHeight}px`;
      
      // After animation, set to auto for dynamic content
      setTimeout(() => {
        if (content.style.height === `${targetHeight}px`) {
          content.style.height = 'auto';
        }
        content.style.overflow = 'visible';
      }, this.animationDuration);
    });
  }

  animateClose(content, contentInner, animate) {
    if (!animate) {
      // No animation - set directly
      content.style.height = '0px';
      content.style.visibility = 'hidden';
      content.style.overflow = 'hidden';
      return;
    }

    // Get current height (use computed height if auto)
    let currentHeight = contentInner.scrollHeight;
    if (content.style.height === 'auto' || !content.style.height) {
      currentHeight = contentInner.scrollHeight;
    } else {
      // Parse current height if it's a pixel value
      const parsed = parseInt(content.style.height, 10);
      if (!isNaN(parsed)) {
        currentHeight = parsed;
      }
    }
    
    // Set height to current
    content.style.height = `${currentHeight}px`;
    content.style.overflow = 'hidden';
    
    // Force reflow
    void content.offsetHeight;
    
    // Animate to 0
    requestAnimationFrame(() => {
      content.style.transition = `height ${this.animationDuration}ms ${this.animationEasing}`;
      content.style.height = '0px';
      
      // After animation, hide
      setTimeout(() => {
        content.style.visibility = 'hidden';
        content.style.overflow = 'hidden';
      }, this.animationDuration);
    });
  }
}

// Initialize all footer accordions
function initFooterAccordions() {
  const containers = document.querySelectorAll('.footer-accordion');
  containers.forEach((container) => {
    if (!container.dataset.initialized) {
      container.dataset.initialized = 'true';
      new FooterAccordion(container);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFooterAccordions);
} else {
  initFooterAccordions();
}

// Re-initialize when sections are loaded in theme editor
document.addEventListener('shopify:section:load', () => {
  initFooterAccordions();
});

document.addEventListener('shopify:block:select', (event) => {
  // Re-initialize when a block is selected in theme editor
  initFooterAccordions();
});
